import React, { useState, useEffect } from 'react';
import { Star, ThumbsUp, Check, AlertCircle, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Component
const StarRating = ({ rating, onRate, interactive = false, size = 'md' }) => {
  const [hovered, setHovered] = useState(0);
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? 'button' : undefined}
          onClick={() => interactive && onRate && onRate(star)}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => interactive && setHovered(0)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          disabled={!interactive}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hovered || rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-gray-200 text-gray-200'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

// Single Review Card
const ReviewCard = ({ review, onHelpful }) => {
  const [helpfulClicked, setHelpfulClicked] = useState(false);

  const handleHelpful = async () => {
    if (helpfulClicked) return;
    try {
      await axios.post(`${API_URL}/api/reviews/${review.id}/helpful`);
      setHelpfulClicked(true);
      onHelpful(review.id);
      toast.success('Thanks for your feedback!');
    } catch (e) {
      console.error('Error marking helpful:', e);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="border-b border-gray-100 py-6 last:border-0" data-testid={`review-${review.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <StarRating rating={review.rating} size="sm" />
            <span className="font-semibold text-[#1A1A1A]">{review.title}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>{review.user_name}</span>
            {review.verified_purchase && (
              <span className="flex items-center gap-1 text-[#3A5A40]">
                <Check className="h-3 w-3" />
                Verified Purchase
              </span>
            )}
            <span>•</span>
            <span>{formatDate(review.created_at)}</span>
          </div>
        </div>
      </div>
      
      <p className="text-gray-700 mb-4">{review.content}</p>
      
      <button
        onClick={handleHelpful}
        disabled={helpfulClicked}
        className={`flex items-center gap-2 text-sm ${
          helpfulClicked 
            ? 'text-[#3A5A40]' 
            : 'text-gray-500 hover:text-[#3A5A40]'
        } transition-colors`}
      >
        <ThumbsUp className={`h-4 w-4 ${helpfulClicked ? 'fill-current' : ''}`} />
        Helpful ({review.helpful_votes + (helpfulClicked ? 1 : 0)})
      </button>
    </div>
  );
};

// Review Form
const ReviewForm = ({ productId, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please sign in to write a review');
        return;
      }

      await axios.post(
        `${API_URL}/api/products/${productId}/reviews`,
        { product_id: productId, rating, title, content },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Review submitted successfully!');
      setRating(0);
      setTitle('');
      setContent('');
      if (onSuccess) onSuccess();
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to submit review';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating</label>
        <StarRating rating={rating} onRate={setRating} interactive size="lg" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Review Title</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Summarize your experience"
          required
          data-testid="review-title-input"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Your Review</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Share your thoughts about this product..."
          required
          className="w-full min-h-[120px] p-3 border rounded-lg resize-y"
          data-testid="review-content-input"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-[#3A5A40] hover:bg-[#2A4030]"
        data-testid="submit-review-btn"
      >
        {loading ? 'Submitting...' : 'Submit Review'}
      </Button>
    </form>
  );
};

// Main Reviews Component
const ProductReviews = ({ productId, productName }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, review_count: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products/${productId}/reviews`);
      setReviews(res.data.reviews || []);
      setStats({
        average_rating: res.data.average_rating || 0,
        review_count: res.data.review_count || 0
      });
    } catch (e) {
      console.error('Error fetching reviews:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleHelpful = (reviewId) => {
    setReviews(prev => prev.map(r => 
      r.id === reviewId ? { ...r, helpful_votes: r.helpful_votes + 1 } : r
    ));
  };

  const ratingDistribution = [5, 4, 3, 2, 1].map(stars => ({
    stars,
    count: reviews.filter(r => r.rating === stars).length,
    percentage: reviews.length > 0 
      ? Math.round((reviews.filter(r => r.rating === stars).length / reviews.length) * 100)
      : 0
  }));

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-32 bg-gray-100 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8" data-testid="product-reviews">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>
          Customer Reviews
        </h2>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button className="bg-[#3A5A40] hover:bg-[#2A4030]" data-testid="write-review-btn">
              <MessageSquare className="h-4 w-4 mr-2" />
              Write a Review
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Write a Review for {productName}</DialogTitle>
            </DialogHeader>
            <ReviewForm 
              productId={productId} 
              onSuccess={() => {
                setShowForm(false);
                fetchReviews();
              }} 
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Rating Summary */}
      <div className="grid md:grid-cols-2 gap-8 mb-8 pb-8 border-b border-gray-100">
        <div className="text-center">
          <div className="text-5xl font-bold text-[#1A1A1A] mb-2">{stats.average_rating.toFixed(1)}</div>
          <StarRating rating={Math.round(stats.average_rating)} size="lg" />
          <p className="text-gray-500 mt-2">Based on {stats.review_count} reviews</p>
        </div>
        <div className="space-y-2">
          {ratingDistribution.map(({ stars, count, percentage }) => (
            <div key={stars} className="flex items-center gap-3">
              <span className="text-sm text-gray-600 w-8">{stars} ★</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-yellow-400 rounded-full transition-all"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="text-sm text-gray-500 w-12">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reviews List */}
      {reviews.length > 0 ? (
        <div className="divide-y divide-gray-100">
          {reviews.map(review => (
            <ReviewCard key={review.id} review={review} onHelpful={handleHelpful} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">No reviews yet</h3>
          <p className="text-gray-500 mb-4">Be the first to share your experience!</p>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-[#3A5A40] hover:bg-[#2A4030]"
          >
            Write the First Review
          </Button>
        </div>
      )}
    </div>
  );
};

export { ProductReviews, StarRating };
export default ProductReviews;
